import { z } from 'zod';
import { AssocPriceType } from '../../constants/assoc-price-type.js';
import { parseFiniteNumber } from '../../parse-finite-number.js';
import { requiredEnumIdString } from '../zod-helpers/index.js';
import { ASSOC_PRICE_CONSTRAINTS } from './constraints.js';
// price is a DimeInput text field client-side (RHF forms carry decimal fields as strings, unlike
// setPriceAddOnInputSchema's z.number()) -- bounds mirror that server schema exactly (required,
// positive, capped at 1,000,000,000) so a value that passes here can never be rejected
// server-side. Unlike the old two-unnamed-field form, a blank price is invalid rather than "no
// adjustment of this type": removing an add-on is now removePriceAddOn (see product-add-on-list),
// not a blank field on save.
const priceFormSchema = z.string().superRefine((value, context) => {
    const trimmed = value.trim();
    if (!trimmed) {
        context.addIssue({
            code: 'custom',
            message: `${ASSOC_PRICE_CONSTRAINTS.price.label} is required.`,
            path: [],
        });
        return;
    }
    const parsed = parseFiniteNumber(trimmed);
    if (parsed === undefined) {
        context.addIssue({
            code: 'custom',
            message: `Enter a valid ${ASSOC_PRICE_CONSTRAINTS.price.label.toLowerCase()}.`,
            path: [],
        });
        return;
    }
    if (parsed <= 0) {
        context.addIssue({
            code: 'custom',
            message: `${ASSOC_PRICE_CONSTRAINTS.price.label} must be greater than 0.`,
            path: [],
        });
        return;
    }
    if (parsed > ASSOC_PRICE_CONSTRAINTS.price.max) {
        context.addIssue({
            code: 'custom',
            message: `${ASSOC_PRICE_CONSTRAINTS.price.label} must be ${ASSOC_PRICE_CONSTRAINTS.price.max.toLocaleString()} or less.`,
            path: [],
        });
    }
});
/**
 * Backs product-add-on-dialog.tsx, the create-or-edit form for a single named price add-on.
 * assocPriceTypeId is carried as a string (DimeRadioGroup values are strings) and validated
 * against AssocPriceType's numeric values via requiredEnumIdString -- matching
 * createLocationInputSchema's typeId, the established pattern for a numeric int-enum FK field
 * bound to a form (see validation/assoc-price/input.ts's own comment citing it). The numeric
 * conversion happens only in toSetPriceAddOnInput (mapper.ts), never here.
 */
export const productAddOnFormFieldsSchema = z.object({
    name: z
        .string()
        .trim()
        .min(1, `${ASSOC_PRICE_CONSTRAINTS.name.label} is required.`)
        .max(ASSOC_PRICE_CONSTRAINTS.name.maxLength, `${ASSOC_PRICE_CONSTRAINTS.name.label} must be ${ASSOC_PRICE_CONSTRAINTS.name.maxLength} characters or fewer.`),
    assocPriceTypeId: requiredEnumIdString(AssocPriceType, `${ASSOC_PRICE_CONSTRAINTS.assocPriceTypeId.label} is required.`, 'Invalid price type selection.'),
    price: priceFormSchema,
    activeFlag: z.boolean(),
});
export const emptyProductAddOnFormValues = () => ({
    name: '',
    assocPriceTypeId: '',
    price: '',
    activeFlag: true,
});
//# sourceMappingURL=form.js.map